import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Eye, ArrowLeft, Globe, Smartphone, Monitor, ShieldCheck, ShieldX, ShieldAlert,
  Mail, MailCheck, Lock, Zap, Ban, Check, TrendingUp, CreditCard, Pencil,
  RefreshCw, KeyRound, Package, Fingerprint, Calendar, Settings,
  Radio, MapPin, ClipboardList, Paperclip, Hourglass, AlertTriangle, FileText,
  Search, ScrollText, X,
} from "lucide-react";

import { getToken, clearAllTokens } from "../../utils/tokenStorage";
import { useIdleTimeout } from "../../hooks/useIdleTimeout";
import IdleWarningModal from "../../components/IdleWarningModal";

const FONT_MONO = "var(--au-font-mono)";

const timeAgo = (ts) => {
  if (!ts) return "—";
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 1) return "just now";
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const flag = (cc) => {
  if (!cc || cc.length !== 2) return null;
  return String.fromCodePoint(...cc.toUpperCase().split("").map((c) => 127397 + c.charCodeAt()));
};

const DeviceIcon = ({ type, size = 13 }) =>
  (type === "mobile" || type === "tablet")
    ? <Smartphone size={size} style={{ verticalAlign: "middle" }} />
    : <Monitor size={size} style={{ verticalAlign: "middle" }} />;

export default function UserDetail() {
  const { userId } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loginHistory, setLoginHistory] = useState([]);
  const [pageHistory, setPageHistory] = useState([]);
  const [billing, setBilling] = useState(null);
  const [docUrl, setDocUrl] = useState(null);
  const [docError, setDocError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const masterToken = getToken("master_token");

  const handleMasterIdleLogout = React.useCallback(() => {
    try { sessionStorage.removeItem("master_token"); localStorage.removeItem("master_token"); } catch(e) {}
    window.location.href = "/mascop-2k26";
  }, []);
  const masterIdle = useIdleTimeout({
    timeoutMinutes: 15,
    warningMinutes: 1,
    onIdle: handleMasterIdleLogout,
    enabled: true
  });

  const loadAll = async () => {
    try {
      const detailsRes = await fetch(`/api/master/users/${userId}/details`, {
        headers: { Authorization: `Bearer ${masterToken}` },
      });
      if (detailsRes.status === 401) {
        clearAllTokens();
        nav("/mascop-2k26");
        return;
      }
      if (!detailsRes.ok) throw new Error(`Status ${detailsRes.status}`);
      const details = await detailsRes.json();
      setData(details);

      // Parallel: login history
      fetch(`/api/master/users/${userId}/login-history?limit=50`, {
        headers: { Authorization: `Bearer ${masterToken}` },
      }).then((r) => (r.ok ? r.json() : []))
        .then((h) => setLoginHistory(Array.isArray(h) ? h : []))
        .catch(() => {});

      // Parallel: billing
      fetch(`/api/master/users/${userId}/billing`, {
        headers: { Authorization: `Bearer ${masterToken}` },
      }).then((r) => (r.ok ? r.json() : null))
        .then((b) => b && setBilling(b))
        .catch(() => {});

      // Parallel: page history
      fetch(`/api/master/users/${userId}/page-history?limit=200`, {
        headers: { Authorization: `Bearer ${masterToken}` },
      }).then((r) => (r.ok ? r.json() : []))
        .then((h) => setPageHistory(Array.isArray(h) ? h : []))
        .catch(() => {});

      // Parallel: KYC document blob
      if (details.kyc?.kyc_id) {
        fetch(`/api/master/kyc/${details.kyc.kyc_id}/document`, {
          headers: { Authorization: `Bearer ${masterToken}` },
        }).then((r) => {
          if (r.ok) return r.blob();
          setDocError(`HTTP ${r.status}`);
          return null;
        }).then((b) => b && setDocUrl(URL.createObjectURL(b)))
          .catch((e) => setDocError(e.message));
      }
    } catch (e) {
      toast.error(e.message || "Failed to load user");
      setTimeout(() => nav("/mascop-2k26/dashboard"), 1500);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!masterToken) { nav("/mascop-2k26"); return; }
    loadAll();
  }, [userId]);

  useEffect(() => () => { if (docUrl) URL.revokeObjectURL(docUrl); }, [docUrl]);

  const masterAction = async (endpoint, payload) => {
    try {
      const res = await fetch(`/api/master/${endpoint}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${masterToken}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Action failed");
      }
      toast.success("Done");
      loadAll();
    } catch (e) { toast.error(e.message); }
  };

  const addCredits = () => setModal({
    title: "Add Credits", desc: `Grant credits to ${user.email}`,
    fields: [
      { key: "amount", label: "Credits (positive integer)", type: "number", placeholder: "e.g. 100" },
      { key: "reason", label: "Reason", type: "text", value: "Admin grant", placeholder: "Beta tester bonus" },
    ],
    confirmLabel: "Grant Credits",
    onConfirm: async (v) => {
      const amount = parseInt(v.amount);
      if (!amount || amount <= 0) { toast.error("Invalid amount"); return false; }
      const res = await fetch(`/api/master/users/${userId}/add-credits`, {
        method: "POST", headers: { Authorization: `Bearer ${masterToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ amount, reason: v.reason || "Admin grant" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      toast.success(`+${data.added} credits. New balance: ${data.new_balance}`); loadAll();
    },
  });

  const forceLogout = () => setModal({
    title: "Force Logout", desc: `Invalidate ALL active sessions for ${user.email}?`,
    danger: true, confirmLabel: "Force Logout",
    onConfirm: async () => {
      const res = await fetch(`/api/master/users/${userId}/force-logout`, {
        method: "POST", headers: { Authorization: `Bearer ${masterToken}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      toast.success("Sessions invalidated"); loadAll();
    },
  });

  const resetPassword = () => setModal({
    title: "Reset Password", desc: `Generate temporary password for ${user.email}? This WILL replace the current password.`,
    danger: true, confirmLabel: "Generate",
    onConfirm: async () => {
      const res = await fetch(`/api/master/users/${userId}/reset-password`, {
        method: "POST", headers: { Authorization: `Bearer ${masterToken}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      try { await navigator.clipboard.writeText(data.temp_password); } catch (e) {}
      setModal({
        title: "Temporary Password",
        desc: "Copied to clipboard. Share securely (SMS/call). User must change after login.",
        result: data.temp_password, confirmLabel: "Done", onConfirm: async () => {},
      });
      return false;
    },
  });

  const editScanLimit = () => {
    const cur = user.scans_limit ?? user.daily_limit ?? "?";
    setModal({
      title: "Edit Scan Limit", desc: `New daily scan limit for ${user.email} (currently: ${cur})`,
      fields: [{ key: "limit", label: "Daily scan limit", type: "number", placeholder: String(cur) }],
      confirmLabel: "Set Limit",
      onConfirm: async (v) => {
        const n = parseInt(v.limit);
        if (isNaN(n) || n < 0) { toast.error("Must be 0 or positive"); return false; }
        const res = await fetch(`/api/master/users/${userId}/scan-limit`, {
          method: "POST", headers: { Authorization: `Bearer ${masterToken}`, "Content-Type": "application/json" },
          body: JSON.stringify({ limit: n }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Failed");
        toast.success(`Limit set to ${n} (${data.field_updated})`); loadAll();
      },
    });
  };

  const sendEmail = () => setModal({
    title: "Send Email", desc: `Send a custom email to ${user.email}`,
    fields: [
      { key: "subject", label: "Subject", type: "text", placeholder: "Email subject" },
      { key: "body", label: "Body", type: "textarea", placeholder: "Email body (plain text)" },
    ],
    confirmLabel: "Send Email",
    onConfirm: async (v) => {
      if (!v.subject?.trim() || !v.body?.trim()) { toast.error("Subject and body required"); return false; }
      const res = await fetch(`/api/master/users/${userId}/send-email`, {
        method: "POST", headers: { Authorization: `Bearer ${masterToken}`, "Content-Type": "application/json" },
        body: JSON.stringify({ subject: v.subject, body: v.body }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      toast.success(`Sent to ${data.sent_to}`);
    },
  });

  const exportData = async () => {
    try {
      const res = await fetch(`/api/master/users/${userId}/export`, {
        headers: { Authorization: `Bearer ${masterToken}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `user_${(user.email || "data").replace(/[^a-z0-9]/gi, "_")}_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("JSON exported");
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return <div className="au-root" style={styles.loading}>Loading user details…</div>;
  if (!data) return <div className="au-root" style={styles.loading}>User not found</div>;

  const { user, stats, kyc, recent_scans, activity_logs } = data;
  const kycStatus = (kyc?.status || "").toLowerCase();

  return (
    <div className="au-root" style={styles.page}>
      {modal && <Modal config={modal} onClose={() => setModal(null)} />}
      <div style={styles.nav}>
        <button onClick={() => nav("/mascop-2k26/dashboard")} className="au-pill au-pill-glass au-pill-sm">
          <ArrowLeft size={16} /> Back to Dashboard
        </button>
        <span style={{ color: "var(--au-error)", fontSize: 11, letterSpacing: 3, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Eye size={14} /> MASTER VIEW
        </span>
      </div>

      <div style={styles.container}>
        {/* HEADER */}
        <div className="au-glass" style={styles.headerCard}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: "var(--au-text)", marginBottom: 4 }}>{user.name || "Unnamed"}</div>
            <div style={{ fontSize: 14, color: "var(--au-accent-ink)", marginBottom: 14, fontFamily: FONT_MONO }}>{user.email}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Badge color="var(--au-info)" text={String(user.plan).split(".").pop()}/>
              {kycStatus === "approved" ? <Badge color="var(--au-success)" icon={<ShieldCheck size={12} />} text="KYC APPROVED"/> :
               kycStatus === "pending" ? <Badge color="var(--au-warn)" icon={<Hourglass size={12} />} text="KYC PENDING"/> :
               kycStatus === "rejected" ? <Badge color="var(--au-error)" icon={<ShieldX size={12} />} text="KYC REJECTED"/> :
               <Badge color="var(--au-muted)" icon={<ShieldAlert size={12} />} text="KYC NOT SUBMITTED"/>}
              {user.is_verified ? <Badge color="var(--au-success)" icon={<MailCheck size={12} />} text="EMAIL VERIFIED"/> : <Badge color="var(--au-warn)" icon={<Mail size={12} />} text="EMAIL PENDING"/>}
              {user.is_banned ? <Badge color="var(--au-error)" icon={<Lock size={12} />} text="BANNED"/> : <Badge color="var(--au-success)" icon={<Check size={12} />} text="ACTIVE"/>}
              {user.is_admin && <Badge color="var(--au-accent-ink)" icon={<Zap size={12} />} text="ADMIN"/>}
              {[...new Set(loginHistory.map(l => l.subdomain).filter(Boolean))].map(s => {
                const m = { aegis: ["var(--au-warn)","AEGIS"], sip: ["var(--au-success)","SIP"], debug: ["var(--au-accent-ink)","BUGSCOUT"] }[s];
                return m ? <Badge key={s} color={m[0]} icon={<Radio size={12} />} text={m[1]}/> : null;
              })}
            </div>
          </div>

        </div>

        {/* ADMIN ACTIONS PANEL — full control */}
        <Card title="Admin Actions — Full Control Panel" titleIcon={<Zap size={14} />}>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 10,
          }}>
            <ActionBtn
              icon={user.is_banned ? <Check size={20} /> : <Ban size={20} />}
              label={user.is_banned ? "Unban User" : "Ban User"}
              desc={user.is_banned ? "Restore account" : "Block account access"}
              color={user.is_banned ? "var(--au-success)" : "var(--au-error)"}
              onClick={() => setModal({
                title: user.is_banned ? "Unban User" : "Ban User",
                desc: `${user.is_banned ? "Unban" : "Ban"} ${user.email}?`,
                danger: !user.is_banned, confirmLabel: user.is_banned ? "Unban" : "Ban",
                onConfirm: async () => { await masterAction(user.is_banned ? "users/unban" : "users/ban", { user_id: user.id }); },
              })}
            />
            <ActionBtn icon={<TrendingUp size={20} />} label="Change Plan"
              desc="Upgrade/downgrade subscription"
              color="var(--au-accent-ink)"
              onClick={() => setModal({
                title: "Change Plan", desc: `Change subscription plan for ${user.email}`,
                fields: [{ key: "plan", label: "Plan", type: "select", value: "FREE",
                  options: ["FREE","PAY_SCAN","STARTER","PRO","PRO_PLUS","AGENCY"] }],
                confirmLabel: "Change Plan",
                onConfirm: async (v) => { await masterAction("users/upgrade", { user_id: user.id, plan: v.plan }); },
              })}
            />
            <ActionBtn icon={<CreditCard size={20} />} label="Add Credits"
              desc="Grant credits (support/goodwill)"
              color="var(--au-success)" onClick={addCredits}/>
            <ActionBtn icon={<Pencil size={20} />} label="Edit Scan Limit"
              desc="Custom daily limit override"
              color="var(--au-info)" onClick={editScanLimit}/>
            <ActionBtn icon={<RefreshCw size={20} />} label="Force Logout"
              desc="Invalidate all active sessions"
              color="var(--au-warn)" onClick={forceLogout}/>
            <ActionBtn icon={<KeyRound size={20} />} label="Reset Password"
              desc="Generate temporary password"
              color="var(--au-warn)" onClick={resetPassword}/>
            <ActionBtn icon={<Mail size={20} />} label="Send Email"
              desc="Custom message to user"
              color="var(--au-info)" onClick={sendEmail}/>
            <ActionBtn icon={<Package size={20} />} label="Export Data"
              desc="Download full JSON dump"
              color="var(--au-accent-ink)" onClick={exportData}/>
          </div>
        </Card>

        {/* STATS */}
        <div style={styles.statsGrid}>
          <Stat icon={<Fingerprint size={22} />} label="Total Logins" value={user.login_count}/>
          <Stat icon={<Globe size={22} />} label="Unique Sessions" value={loginHistory.length}/>
          <Stat icon={<Search size={22} />} label="Total Scans" value={stats?.total_scans}/>
          <Stat icon={<Calendar size={22} />} label="Member Since" value={user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"}/>
          <Stat icon={<Settings size={22} />} label="Scan Limit" value={user.scans_limit > 0 ? user.scans_limit : (user.plan_quota ?? "Plan default")}/>
        </div>

        {/* CURRENT PRESENCE (if active) */}
        {user.current_page && user.last_activity_at && (
          <Card title="Currently Active" titleIcon={<Zap size={14} />}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
              <Field label="Current Page" value={user.current_page} mono/>
              <Field label="Last Heartbeat" value={timeAgo(user.last_activity_at)}/>
              <Field label="Last IP" value={user.last_ip || "—"} mono/>
            </div>
          </Card>
        )}

        {/* LAST SESSION INFO */}
        <Card title="Last Known Session" titleIcon={<Radio size={14} />}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="Last Login" value={user.last_login ? new Date(user.last_login).toLocaleString() : "Never"}/>
            <Field label="Last IP" value={user.last_ip || "Unknown"} mono/>
          </div>
          {loginHistory[0] && (
            <>
              <Field label="Location"
                value={`${flag(loginHistory[0].country_code) || ""} ${loginHistory[0].city || "?"}, ${loginHistory[0].region || ""} ${loginHistory[0].country || ""}`.trim()}/>
              <Field label="ISP / Network" value={loginHistory[0].isp || "Unknown"}/>
              <Field label="Device"
                value={`${(loginHistory[0].device_type || "desktop").toUpperCase()} · ${loginHistory[0].browser || "?"} on ${loginHistory[0].os || "?"}`}/>
            </>
          )}
          <Field label="Last User Agent" value={user.last_user_agent || "Unknown"} small mono/>
          <Field label="User ID" value={user.id} mono small/>
        </Card>

        {/* LOGIN HISTORY — NEW */}
        {loginHistory.length > 0 && (
          <Card title={`Login History — ${loginHistory.length} unique sessions`} titleIcon={<Globe size={14} />}>
            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 800 }}>
                <div style={styles.historyHeader}>
                  <span></span><span>IP / ISP</span><span>Location</span><span>Device</span>
                  <span>First Seen</span><span style={{ textAlign: "right" }}>Last Seen</span>
                </div>
                {loginHistory.map((evt) => (
                  <div key={evt.id} style={styles.historyRow}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: evt.is_active ? "var(--au-led)" : "var(--au-muted)", boxShadow: evt.is_active ? "0 0 6px var(--au-led)" : "none" }}/>
                    <div>
                      <div style={{ color: "var(--au-text)", fontFamily: FONT_MONO, fontSize: 12 }}>{evt.ip_address}</div>
                      <div style={{ color: "var(--au-muted)", fontSize: 10, marginTop: 2 }}>{evt.isp || "Unknown ISP"}</div>
                    </div>
                    <div>
                      <div style={{ color: "var(--au-text)", display: "flex", gap: 6, alignItems: "center", fontSize: 12 }}>
                        <span style={{ fontSize: 14 }}>{flag(evt.country_code) || <Globe size={12} />}</span>
                        <span>{evt.city || "Unknown"}</span>
                      </div>
                      <div style={{ color: "var(--au-muted)", fontSize: 10, marginTop: 2 }}>{evt.region || ""} {evt.country || ""}</div>
                    </div>
                    <div>
                      <div style={{ color: "var(--au-text)", fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}><DeviceIcon type={evt.device_type} /> {evt.browser || "?"}</div>
                      <div style={{ color: "var(--au-muted)", fontSize: 10, marginTop: 2 }}>{evt.os || "?"}</div>
                    </div>
                    <div style={{ color: "var(--au-text-2)", fontSize: 11 }}>{timeAgo(evt.first_seen)}</div>
                    <div style={{ color: "var(--au-text-2)", fontSize: 11, textAlign: "right" }}>{timeAgo(evt.last_seen)}</div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

        {/* PAGES VISITED — page tracking */}
        {pageHistory.length > 0 && (
          <Card title={`Pages Visited — ${pageHistory.length} entries`} titleIcon={<MapPin size={14} />}>
            <div style={{ maxHeight: 360, overflowY: "auto", display: "grid", gap: 6 }}>
              {pageHistory.map((p) => (
                <div key={p.id} style={{
                  display: "grid",
                  gridTemplateColumns: "1.5fr 1fr 1fr",
                  gap: 12, alignItems: "center",
                  padding: "10px 14px",
                  background: "var(--au-glass)", borderRadius: 6,
                  border: "1px solid var(--au-hair)", fontSize: 12,
                }}>
                  <span style={{
                    fontFamily: FONT_MONO, color: "var(--au-accent-ink)",
                    background: "var(--au-glass-strong)", padding: "3px 8px",
                    borderRadius: 4, display: "inline-block",
                    width: "fit-content",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>{p.page}</span>
                  <span style={{ fontFamily: FONT_MONO, color: "var(--au-muted)", fontSize: 11 }}>{p.ip_address || "—"}</span>
                  <span style={{ color: "var(--au-text-2)", fontSize: 11, textAlign: "right" }}>
                    {p.visited_at ? new Date(p.visited_at).toLocaleString() : "—"}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* KYC SECTION */}
        {kyc && (
          <Card title="KYC Verification" titleIcon={<ClipboardList size={14} />}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              <div>
                <Field label="Status" value={kycStatus.toUpperCase()}
                  color={kycStatus === "approved" ? "var(--au-success)" : kycStatus === "rejected" ? "var(--au-error)" : "var(--au-warn)"}/>
                <Field label="Full Name" value={kyc.full_name}/>
                <Field label="Phone" value={kyc.phone}/>
                <Field label="Country" value={kyc.country}/>
                <Field label="Document Type" value={kyc.document_type}/>
                <Field label="Doc Last 4" value={kyc.doc_last4 ? `XXXX-XXXX-${kyc.doc_last4}` : "—"} mono/>
                <Field label="Address" value={kyc.address}/>
                <Field label="City / State" value={`${kyc.city || "—"}, ${kyc.state || "—"}`}/>
                <Field label="ZIP" value={kyc.zipcode}/>
                <Field label="Account Type" value={kyc.account_type}/>
                {kyc.company_name && <Field label="Company" value={kyc.company_name}/>}
                <Field label="Submitted" value={kyc.submitted_at ? new Date(kyc.submitted_at).toLocaleString() : "—"}/>
                {kyc.verified_at && <Field label="Verified" value={new Date(kyc.verified_at).toLocaleString()}/>}
                {kyc.rejection_reason && <Field label="Rejection Reason" value={kyc.rejection_reason} color="var(--au-error)"/>}
              </div>

              <div>
                <div style={{ fontSize: 11, color: "var(--au-muted)", letterSpacing: 2, marginBottom: 8, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}><Paperclip size={12} /> UPLOADED DOCUMENT</div>
                {docUrl ? (
                  <div style={{ background: "#000", borderRadius: 8, border: "1px solid var(--au-border)", overflow: "hidden", padding: 8 }}>
                    <img src={docUrl} alt="KYC Document"
                      style={{ width: "100%", height: "auto", borderRadius: 4, cursor: "pointer", display: "block" }}
                      onClick={() => window.open(docUrl, "_blank")}/>
                    <div style={{ marginTop: 8, fontSize: 11, color: "var(--au-text-2)", textAlign: "center" }}>Click to view full size</div>
                  </div>
                ) : docError ? (
                  <div style={{ padding: 20, background: "rgba(242,80,60,0.06)", border: "1px solid rgba(242,80,60,0.30)", borderRadius: 8, color: "var(--au-error)", fontSize: 12, lineHeight: 1.7, display: "flex", gap: 8 }}>
                    <AlertTriangle size={16} style={{ flex: "none", marginTop: 2 }} />
                    <div>
                      Document not loadable<br/>
                      <span style={{ color: "var(--au-muted)", fontSize: 11 }}>
                        Endpoint returned: <code>{docError}</code><br/>
                        KYC ID: <code style={{ fontFamily: FONT_MONO }}>{kyc.kyc_id}</code><br/>
                        File may not exist on server, or path mismatch in DB.
                      </span>
                    </div>
                  </div>
                ) : (
                  <div style={{ padding: 20, background: "rgba(244,190,80,0.06)", border: "1px solid rgba(244,190,80,0.30)", borderRadius: 8, color: "var(--au-warn)", fontSize: 12, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <Hourglass size={14} /> Loading document…
                  </div>
                )}

                {kycStatus === "pending" && kyc.kyc_id && (
                  <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                    <button onClick={() => {
                        // KYC approve proceeds on click (confirm removed - was browser-blocked)
                        masterAction("kyc/approve", { kyc_id: kyc.kyc_id });
                      }}
                      style={{ flex: 1, padding: 12, background: "rgba(41,191,18,0.15)", border: "1px solid var(--au-success)", color: "var(--au-success)", borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: "pointer", letterSpacing: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: "inherit" }}>
                      <Check size={15} /> APPROVE
                    </button>
                    <button onClick={() => {
                        const reason = window.prompt("Rejection reason:");
                        if (!reason?.trim()) return;
                        masterAction("kyc/reject", { kyc_id: kyc.kyc_id, reason: reason.trim() });
                      }}
                      style={{ flex: 1, padding: 12, background: "rgba(242,80,60,0.15)", border: "1px solid var(--au-error)", color: "var(--au-error)", borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: "pointer", letterSpacing: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, fontFamily: "inherit" }}>
                      <X size={15} /> REJECT
                    </button>
                  </div>
                )}

                <div style={{ marginTop: 14, padding: 12, background: "var(--au-glass-strong)", border: "1px solid var(--au-border)", borderRadius: 6, fontSize: 11, color: "var(--au-text-2)", lineHeight: 1.7 }}>
                  <strong style={{ color: "var(--au-accent-ink)", display: "inline-flex", alignItems: "center", gap: 6 }}><AlertTriangle size={13} /> Verification Checklist:</strong><br/>
                  • Document type matches submitted info<br/>
                  • Name matches user's account<br/>
                  • Photo is clear and not tampered<br/>
                  • Government-issued ID<br/>
                  • Information looks genuine
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* BILLING SECTION */}
        {billing && (billing.credit_transactions?.length > 0 || billing.payments?.length > 0 || billing.invoices?.length > 0) && (
          <Card title={`Billing & Credits — ₹${billing.summary?.total_paid_inr || 0} paid · ${billing.summary?.credit_tx_count || 0} credit txns · ${billing.summary?.invoice_count || 0} invoices`} titleIcon={<CreditCard size={14} />}>

            {billing.credit_transactions?.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, color: "var(--au-muted)", letterSpacing: 1.5, marginBottom: 10, fontWeight: 700, textTransform: "uppercase" }}>
                  Credit Transactions ({billing.credit_transactions.length})
                </div>
                <div style={{ maxHeight: 300, overflowY: "auto", display: "grid", gap: 4 }}>
                  {billing.credit_transactions.map((t) => (
                    <div key={t.id} style={{
                      display: "grid",
                      gridTemplateColumns: "0.9fr 0.7fr 1fr 0.9fr 1.4fr",
                      gap: 12, alignItems: "center",
                      padding: "8px 12px",
                      background: "var(--au-glass)", borderRadius: 6,
                      border: "1px solid var(--au-hair)", fontSize: 11,
                    }}>
                      <span style={{
                        fontWeight: 700, color: t.transaction_type === "PURCHASE" ? "var(--au-success)" :
                                              t.transaction_type === "USAGE" ? "var(--au-warn)" : "var(--au-info)",
                        letterSpacing: 0.5,
                      }}>{t.transaction_type}</span>
                      <span style={{ fontFamily: FONT_MONO, color: t.amount > 0 ? "var(--au-success)" : "var(--au-error)", fontWeight: 700 }}>
                        {t.amount > 0 ? "+" : ""}{t.amount}
                      </span>
                      <span style={{ fontFamily: FONT_MONO, color: "var(--au-text-2)", fontSize: 10 }}>
                        bal: {t.balance_before} → {t.balance_after}
                      </span>
                      <span style={{ color: "var(--au-muted)", fontSize: 10 }}>
                        {t.razorpay_payment_id ? `₹${(t.razorpay_amount_paise || 0) / 100}` : (t.ai_provider || "—")}
                      </span>
                      <span style={{ color: "var(--au-muted)", fontSize: 10, textAlign: "right" }}>
                        {t.reason || "—"}<br/>
                        <span style={{ fontSize: 9 }}>{t.created_at ? new Date(t.created_at).toLocaleString() : "—"}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {billing.payments?.length > 0 && (
              <div style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, color: "var(--au-muted)", letterSpacing: 1.5, marginBottom: 10, fontWeight: 700, textTransform: "uppercase" }}>
                  Payments ({billing.payments.length})
                </div>
                <div style={{ display: "grid", gap: 4 }}>
                  {billing.payments.map((p) => (
                    <div key={p.id} style={{
                      display: "grid",
                      gridTemplateColumns: "0.8fr 1.2fr 0.7fr 1fr 1.3fr",
                      gap: 12, alignItems: "center",
                      padding: "8px 12px",
                      background: "var(--au-glass)", borderRadius: 6,
                      border: "1px solid var(--au-hair)", fontSize: 11,
                    }}>
                      <span style={{ color: "var(--au-accent-ink)", fontWeight: 700 }}>₹{p.amount}</span>
                      <span style={{ color: "var(--au-text)", fontSize: 11 }}>{p.plan}</span>
                      <span style={{ color: p.status === "SUCCESS" ? "var(--au-success)" : "var(--au-error)", fontWeight: 600, fontSize: 10 }}>{p.status}</span>
                      <span style={{ fontFamily: FONT_MONO, color: "var(--au-muted)", fontSize: 9 }}>{p.gateway_txn_id || "—"}</span>
                      <span style={{ color: "var(--au-muted)", fontSize: 10, textAlign: "right" }}>
                        {p.gateway}<br/>{p.created_at ? new Date(p.created_at).toLocaleString() : "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {billing.invoices?.length > 0 && (
              <div>
                <div style={{ fontSize: 11, color: "var(--au-muted)", letterSpacing: 1.5, marginBottom: 10, fontWeight: 700, textTransform: "uppercase" }}>
                  Invoices ({billing.invoices.length})
                </div>
                <div style={{ display: "grid", gap: 4 }}>
                  {billing.invoices.map((inv) => (
                    <div key={inv.id} style={{
                      display: "grid",
                      gridTemplateColumns: "1.2fr 0.7fr 0.7fr 0.7fr 1.2fr 0.7fr",
                      gap: 12, alignItems: "center",
                      padding: "8px 12px",
                      background: "var(--au-glass)", borderRadius: 6,
                      border: "1px solid var(--au-hair)", fontSize: 11,
                    }}>
                      <span style={{ fontFamily: FONT_MONO, color: "var(--au-info)" }}>{inv.invoice_number}</span>
                      <span style={{ color: "var(--au-accent-ink)", fontWeight: 700 }}>₹{(inv.amount_paise || 0) / 100}</span>
                      <span style={{ color: "var(--au-text)" }}>{inv.credits_purchased} cr</span>
                      <span style={{ color: "var(--au-success)", fontSize: 10 }}>{inv.status}</span>
                      <span style={{ color: "var(--au-muted)", fontSize: 10 }}>{inv.payment_method || "—"} · {inv.created_at ? new Date(inv.created_at).toLocaleString() : ""}</span>
                      {inv.pdf_path ? (
                        <a href={inv.pdf_path} target="_blank" rel="noreferrer"
                          style={{ color: "var(--au-accent-ink)", fontSize: 10, textAlign: "right", display: "inline-flex", alignItems: "center", justifyContent: "flex-end", gap: 4 }}><FileText size={11} /> PDF</a>
                      ) : <span style={{ textAlign: "right", color: "var(--au-muted)" }}>—</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* RECENT SCANS */}
        <Card title={`Recent Scans (${recent_scans?.length || 0})`} titleIcon={<Search size={14} />}>
          {!recent_scans || recent_scans.length === 0 ? (
            <div style={{ color: "var(--au-muted)", fontSize: 13, padding: 14 }}>No scans yet</div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {recent_scans.map((s) => (
                <div key={s.id} style={styles.scanRow}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: "var(--au-text)", fontSize: 13, fontFamily: FONT_MONO }}>{s.target}</div>
                    <div style={{ color: "var(--au-muted)", fontSize: 11, marginTop: 2 }}>
                      {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    {s.risk_score !== null && (
                      <div style={{ color: s.risk_score >= 70 ? "var(--au-error)" : s.risk_score >= 40 ? "var(--au-warn)" : "var(--au-success)", fontWeight: 700, fontFamily: FONT_MONO }}>
                        {s.risk_score}/100
                      </div>
                    )}
                    <Badge color={s.status === "DONE" ? "var(--au-success)" : s.status === "FAILED" ? "var(--au-error)" : "var(--au-warn)"} text={s.status} small/>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* ACTIVITY TIMELINE */}
        {activity_logs && activity_logs.length > 0 && (
          <Card title={`Activity Timeline (${activity_logs.length})`} titleIcon={<ScrollText size={14} />}>
            <div style={{ display: "grid", gap: 6, maxHeight: 400, overflowY: "auto" }}>
              {activity_logs.map((log, i) => (
                <div key={i} style={styles.activityRow}>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: "var(--au-info)", fontSize: 12, fontWeight: 600 }}>{log.action}</div>
                    <div style={{ color: "var(--au-text-2)", fontSize: 11, marginTop: 2 }}>{log.details || "—"}</div>
                  </div>
                  <div style={{ textAlign: "right", fontSize: 10, color: "var(--au-muted)", fontFamily: FONT_MONO }}>
                    <div>{log.timestamp ? new Date(log.timestamp).toLocaleString() : "—"}</div>
                    {log.ip && <div style={{ marginTop: 2 }}>{log.ip}</div>}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

function Badge({ color, text, small, icon }) {
  return (
    <span className="au-chip au-chip-status" style={{
      padding: small ? "3px 8px" : "5px 12px",
      fontSize: small ? 9 : 10,
      fontWeight: 700, letterSpacing: 1.5,
      color, background: "var(--au-glass-strong)",
      border: `1px solid ${color}`,
      textTransform: "uppercase",
    }}>{icon}{text}</span>
  );
}

function Modal({ config, onClose }) {
  const [values, setValues] = useState(() => {
    const init = {};
    (config.fields || []).forEach(f => { init[f.key] = f.value ?? (f.type === "select" ? (f.options?.[0] || "") : ""); });
    return init;
  });
  const [submitting, setSubmitting] = useState(false);
  const inputClass = "au-input";
  const doConfirm = async () => {
    setSubmitting(true);
    try { const r = await config.onConfirm(values); if (r !== false) onClose(); }
    catch (e) { toast.error(e.message || "Action failed"); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="au-root" onClick={onClose} role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20, minHeight: "auto" }}>
      <div onClick={e => e.stopPropagation()} className="au-glass au-pad-lg" style={{ maxWidth: 440, width: "100%", boxShadow: "var(--au-shadow-float)" }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--au-text)", marginBottom: 8 }}>{config.title}</div>
        {config.desc && <div style={{ fontSize: 13, color: "var(--au-text-2)", lineHeight: 1.6, marginBottom: 20 }}>{config.desc}</div>}
        {config.result && <div style={{ background: "var(--au-glass)", border: "1px solid var(--au-border)", borderRadius: 8, padding: 14, marginBottom: 20, fontFamily: FONT_MONO, fontSize: 16, color: "var(--au-accent-ink)", fontWeight: 700, textAlign: "center", wordBreak: "break-all" }}>{config.result}</div>}
        {(config.fields || []).map(f => (
          <div key={f.key} style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 12, color: "var(--au-text-2)", marginBottom: 6, fontWeight: 600 }}>{f.label}</label>
            {f.type === "select" ? (
              <select value={values[f.key]} onChange={e => setValues(s => ({ ...s, [f.key]: e.target.value }))} className={inputClass}>
                {f.options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : f.type === "textarea" ? (
              <textarea value={values[f.key]} onChange={e => setValues(s => ({ ...s, [f.key]: e.target.value }))} placeholder={f.placeholder} rows={4} className={inputClass} style={{ resize: "vertical" }} />
            ) : (
              <input type={f.type === "number" ? "number" : "text"} value={values[f.key]} onChange={e => setValues(s => ({ ...s, [f.key]: e.target.value }))} placeholder={f.placeholder} className={inputClass} />
            )}
          </div>
        ))}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          {!config.result && <button onClick={onClose} disabled={submitting} className="au-pill au-pill-ghost au-pill-sm">Cancel</button>}
          <button onClick={doConfirm} disabled={submitting}
            className={config.danger ? "au-pill au-pill-glass au-pill-sm" : "au-pill au-pill-primary au-pill-sm"}
            style={config.danger
              ? { color: "var(--au-error)", borderColor: "var(--au-error)", fontWeight: 700, opacity: submitting ? 0.6 : 1 }
              : { fontWeight: 700, opacity: submitting ? 0.6 : 1 }}>
            {submitting ? "..." : (config.confirmLabel || "Confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, label, value }) {
  const v = value == null ? "0" : String(value);
  // Plain integers keep the big stat font; text values (dates, "Plan default")
  // shrink so they don't wrap or dwarf the card.
  const isNumeric = /^\d+$/.test(v.trim());
  return (
    <div className="au-glass" style={styles.statCard}>
      <div style={{ marginBottom: 8, color: "var(--au-accent-ink)", display: "flex", justifyContent: "center" }}>{icon}</div>
      <div className="au-stat-lbl" style={{ marginBottom: 4 }}>{label}</div>
      <div className="au-stat-num" style={{ fontFamily: FONT_MONO, ...(isNumeric ? {} : { fontSize: "1.05rem", lineHeight: 1.3 }) }}>{v}</div>
    </div>
  );
}

function Card({ title, titleIcon, children }) {
  return (
    <div className="au-glass au-pad" style={styles.card}>
      <div style={{ fontSize: 11, color: "var(--au-accent-ink)", letterSpacing: 2, marginBottom: 14, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>{titleIcon}{title}</div>
      <div>{children}</div>
    </div>
  );
}

function ActionBtn({ icon, label, desc, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      background: "var(--au-glass)",
      border: `1px solid ${color}`,
      borderRadius: 8,
      padding: "14px 16px",
      cursor: "pointer",
      textAlign: "left",
      transition: "all 0.15s ease",
      display: "flex",
      flexDirection: "column",
      gap: 6,
      fontFamily: "inherit",
    }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "var(--au-glass-strong)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "var(--au-glass)";
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ color, display: "inline-flex" }}>{icon}</span>
        <span style={{ color, fontWeight: 700, fontSize: 13, letterSpacing: 0.3 }}>{label}</span>
      </div>
      <span style={{ color: "var(--au-muted)", fontSize: 11, lineHeight: 1.4 }}>{desc}</span>
    </button>
  );
}

function Field({ label, value, mono, small, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", background: "var(--au-glass)", borderRadius: 6, marginBottom: 8, gap: 14, flexWrap: "wrap" }}>
      <span style={{ color: "var(--au-text-2)", fontSize: 12, letterSpacing: 1, fontWeight: 600, minWidth: 100 }}>{label}</span>
      <span style={{
        color: color || "var(--au-text)",
        fontSize: small ? 11 : 13,
        fontFamily: mono ? FONT_MONO : "inherit",
        wordBreak: "break-all", textAlign: "right", flex: 1,
      }}>{value || "—"}</span>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", color: "var(--au-text)" },
  loading: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--au-accent-ink)" },
  nav: { background: "var(--au-glass)", borderBottom: "1px solid var(--au-hair)", padding: "16px 28px", display: "flex", alignItems: "center", justifyContent: "space-between", backdropFilter: "var(--au-blur)", WebkitBackdropFilter: "var(--au-blur)" },
  container: { maxWidth: 1180, margin: "0 auto", padding: "30px 24px" },
  headerCard: { borderRadius: "var(--au-r-lg)", padding: 32, marginBottom: 24, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 20 },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14, marginBottom: 24 },
  statCard: { padding: 20, textAlign: "center" },
  card: { marginBottom: 18 },
  scanRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "var(--au-glass)", borderRadius: 6, gap: 14, flexWrap: "wrap", border: "1px solid var(--au-hair)" },
  activityRow: { display: "flex", justifyContent: "space-between", padding: "8px 14px", background: "var(--au-glass)", borderRadius: 5, gap: 10, border: "1px solid var(--au-hair)" },
  historyHeader: {
    display: "grid",
    gridTemplateColumns: "20px 1.4fr 1.4fr 1.3fr 1fr 1fr",
    gap: 12, padding: "10px 14px",
    fontSize: 10, color: "var(--au-text-2)", fontWeight: 700,
    letterSpacing: 1.5, textTransform: "uppercase",
    borderBottom: "1px solid var(--au-hair)", marginBottom: 8,
  },
  historyRow: {
    display: "grid",
    gridTemplateColumns: "20px 1.4fr 1.4fr 1.3fr 1fr 1fr",
    gap: 12, alignItems: "center", padding: "12px 14px",
    background: "var(--au-glass)", borderRadius: 6,
    border: "1px solid var(--au-hair)", marginBottom: 6, fontSize: 12,
  },
};
